import i18n from '@/i18n';
import { formatRelativeFromNow, getMerchantTz } from './datetime';

/**
 * Compact `YYYY-MM-DD` form in the merchant timezone, suitable for
 * table cells where space matters and operators need a stable
 * sortable display. For locale-formatted dates use `formatDate` from
 * `lib/datetime.ts` (e.g. `Apr 28, 2026`).
 */
export function formatDate(value: string | number | Date | null | undefined) {
  if (!value) return '-';
  // A `YYYY-MM-DD` wire value is a calendar day with no timezone
  // semantics (jobs shipping_date, orders delivery_date, …). This
  // helper already emits the `YYYY-MM-DD` shape, so pass it through
  // verbatim: routing it via `new Date()` + merchant tz would shift
  // the displayed day back one under PT (#1219 — same fix as #1160
  // in `lib/datetime.ts`).
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  // `en-CA` produces ISO-shaped `YYYY-MM-DD`; locale here is for
  // formatting style, not language. Wall-clock day is anchored to
  // the merchant timezone via the `timeZone` option.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: getMerchantTz(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * Compact `YYYY-MM-DD HH:mm:ss` form in the merchant timezone. Used in
 * table cells / record details where the locale-aware `formatDateTime`
 * from `lib/datetime.ts` is too wide. The two names are deliberately
 * different so an `import` mistake doesn't silently swap formats.
 */
export function formatDateTimeCompact(value: string | number | Date | null | undefined) {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const tz = getMerchantTz();
  const dateStr = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
  const timeStr = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
  return `${dateStr} ${timeStr}`;
}

export function formatDatetimeSince(value?: string | null) {
  return formatRelativeFromNow(value);
}

export function formatDuration(milliseconds: number) {
  const minutes = Math.floor(milliseconds / 60000);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  return `${minutes}m`;
}

export function formatCurrency(amount: number, currency: string = 'USD') {
  return new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: currency,
  }).format(amount);
}

/**
 * Locale-aware number formatter — the plain-number sibling of
 * `formatCurrency`. Groups thousands and applies fraction rules per the
 * active i18n locale (never a hardcoded locale). Pass any
 * `Intl.NumberFormatOptions` (e.g. `{ maximumFractionDigits: 3 }`) for
 * quantity/decimal display. Use this instead of bare `toLocaleString()`
 * / `toFixed()` for any user-visible number (#1290).
 */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions) {
  return new Intl.NumberFormat(i18n.language, options).format(value);
}
